/**
 * InsightsPanel - left-collapsible panel for the Insights Collector.
 *
 * Uses CollapsibleSidePanel for the shell behaviour (toggle, collapse animation,
 * left-docked positioning) and renders insight cards in its body.
 */
import React, { useState, useRef, useMemo, useCallback } from 'react';
import LazyImage from '../ui/LazyImage';
import { createPortal } from 'react-dom';
import { useStore } from 'store/useStore';
import { InsightNode } from 'types';
import InsightCard, { InsightDragInfo } from './InsightCard';
import CollapsibleSidePanel from './CollapsibleSidePanel';
import useDragSortList from 'hooks/useDragSortList';
import { DRAG_SORT_INDEX_ATTR, dragSortItemClassName, dragSortTranslateStyle } from 'utils/dragSortList';

/** Matches `.insights-list`'s own CSS `gap` (app.css) — see getDragSortTranslateY's doc comment. */
const INSIGHTS_LIST_GAP_PX = 4;

export function getUniqueInsightUserNames(insights: InsightNode[] = []): string[] {
    const normalized = new Map<string, string>();

    insights.forEach((insight) => {
        const rawName = insight.sourceUser?.name ?? '';
        const name = rawName.trim();
        if (!name) return;

        const key = name.toLowerCase();
        if (!normalized.has(key)) {
            normalized.set(key, name);
        }
    });

    return Array.from(normalized.values()).sort((a, b) => a.localeCompare(b));
}

export default function InsightsPanel() {
    const {
        insights: rawInsights,
        insightLinks: rawInsightLinks,
        insightsPanelMinimized,
        reorderInsights,
        setInsightsPanelMinimized,
        beginInsightLinkDrag,
        suppressInsightsOpenUntil,
    } = useStore();

    const insights = useMemo(() => rawInsights ?? [], [rawInsights]);
    const insightLinks = useMemo(() => rawInsightLinks ?? [], [rawInsightLinks]);

    const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
    const [selectedFilterUser, setSelectedFilterUser] = useState('');
    const [isFiltered, setIsFiltered] = useState(false);

    const availableUsers = useMemo(() => getUniqueInsightUserNames(insights), [insights]);
    const filteredInsights = useMemo(() => {
        if (!isFiltered || !selectedFilterUser) {
            return insights;
        }

        return insights.filter((insight) => insight.sourceUser?.name?.trim() === selectedFilterUser);
    }, [insights, isFiltered, selectedFilterUser]);

    const panelRef = useRef<HTMLDivElement>(null);

    const handleReorder = useCallback(
        (fromIndex: number, toIndex: number) => reorderInsights(fromIndex, toIndex),
        [reorderInsights],
    );
    const { dragState, beginDrag, getTranslateY } = useDragSortList({
        itemCount: insights.length,
        containerRef: panelRef,
        onReorder: handleReorder,
        gap: INSIGHTS_LIST_GAP_PX,
    });

    const handleItemDragHandleMouseDown = useCallback(
        (e: React.MouseEvent, insightId: string) => {
            const index = insights.findIndex((n) => n.id === insightId);
            beginDrag(e, index);
        },
        [insights, beginDrag],
    );

    const grabbingCursor = `url(${process.env.PUBLIC_URL || ''}/hand-pinch-cursor.cur) 16 16, grabbing`;

    const handleDragFromLink = useCallback(
        (e: React.MouseEvent, info: InsightDragInfo) => {
            if (dragState) return;
            // Ensure the pinch cursor appears immediately on mousedown
            try {
                document.body.style.cursor = grabbingCursor;
                document.body.classList.add('dragging-pinch');
            } catch (err) {
                // ignore in non-browser test environments
            }

            beginInsightLinkDrag({
                insightId: info.insightId,
                existingLinkId: info.linkId,
                selectedText: info.text,
                selectionStart: info.start,
                selectionEnd: info.end,
                startScreenX: e.clientX,
                startScreenY: e.clientY,
            });
        },
        [beginInsightLinkDrag, dragState, grabbingCursor],
    );

    const isCollapsed = insightsPanelMinimized ?? false;
    const toggleCollapse = useCallback(() => setInsightsPanelMinimized(!isCollapsed), [isCollapsed, setInsightsPanelMinimized]);

    const shouldSuppressOpen = useCallback(
        () => !!(suppressInsightsOpenUntil && Date.now() < suppressInsightsOpenUntil),
        [suppressInsightsOpenUntil],
    );

    return (
        <CollapsibleSidePanel
            ref={panelRef}
            side='left'
            isCollapsed={isCollapsed}
            onToggleCollapse={toggleCollapse}
            shouldSuppressOpen={shouldSuppressOpen}
            className='insights-panel'
            ariaLabelOpen='Open insights'
            ariaLabelClose='Collapse insights'
        >
            <div className='insights-panel-inner'>
                <div className='insights-panel-header'>
                    <span className='insights-panel-title'>
                        Insights
                        {insights.length > 0 && <span className='insights-panel-count'>{insights.length}</span>}
                    </span>

                    <div className='insights-panel-header-actions'>
                        {!isCollapsed && (
                            <button
                                className={`insights-header-btn insights-header-btn--search ${isFiltered ? 'insights-header-btn--active' : ''}`}
                                onClick={() => setIsFilterPanelOpen((v) => !v)}
                                title='Filter insights by user'
                                aria-label='Filter insights by user'
                            >
                                <LazyImage
                                    src={`${process.env.PUBLIC_URL || ''}/magnifying-glass-icon-26765.png`}
                                    alt='Filter'
                                    className='insights-header-btn__icon'
                                    style={{ width: '14px', height: '14px' }}
                                />
                            </button>
                        )}
                        <button
                            className='insights-header-btn insights-header-btn--close'
                            onClick={() => setInsightsPanelMinimized(true)}
                            title='Close insights'
                            aria-label='Close insights'
                        >
                            ‹
                        </button>
                    </div>
                </div>

                {!isCollapsed && isFilterPanelOpen && (
                    <div className='insights-filter-panel'>
                        <div className='insights-filter-panel__title'>Filter insights by user</div>
                        <select
                            className='insights-filter-panel__select'
                            value={selectedFilterUser}
                            onChange={(e) => setSelectedFilterUser(e.target.value)}
                        >
                            <option value=''>-- Select user --</option>
                            {availableUsers.map((name) => (
                                <option key={name} value={name}>
                                    {name}
                                </option>
                            ))}
                        </select>
                        <div className='insights-filter-panel__actions'>
                            <button
                                className='insights-filter-btn'
                                onClick={() => {
                                    if (selectedFilterUser) {
                                        setIsFiltered(true);
                                    } else {
                                        setIsFiltered(false);
                                    }
                                    setIsFilterPanelOpen(false);
                                }}
                                disabled={!selectedFilterUser}
                            >
                                Filter
                            </button>
                            <button
                                className='insights-filter-btn insights-filter-btn--clear'
                                onClick={() => {
                                    setIsFiltered(false);
                                    setSelectedFilterUser('');
                                    setIsFilterPanelOpen(false);
                                }}
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                )}

                {!isCollapsed && (
                    <div className='insights-panel-body'>
                        {(filteredInsights.length === 0 || insights.length === 0) && (
                            <p className='insights-empty-hint'>
                                {insights.length === 0 ? 'No insights yet.' : 'No insights match the selected user.'}
                            </p>
                        )}

                        <div className='insights-list'>
                            {filteredInsights.map((insight) => {
                                const insightIndex = insights.findIndex((item) => item.id === insight.id);
                                const translateY = getTranslateY(insightIndex);
                                const isBeingDragged = dragState?.fromIndex === insightIndex;
                                return (
                                    <div
                                        key={insight.id}
                                        {...{ [DRAG_SORT_INDEX_ATTR]: insightIndex }}
                                        className={dragSortItemClassName('insights-list-item', !!dragState, isBeingDragged)}
                                        style={dragSortTranslateStyle(translateY)}
                                    >
                                        <InsightCard
                                            insight={insight}
                                            insightLinks={insightLinks.filter((l) => l.insightId === insight.id)}
                                            onDragHandleMouseDown={handleItemDragHandleMouseDown}
                                            onDragFromLink={handleDragFromLink}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {dragState && insights[dragState.fromIndex] &&
                createPortal(
                    <div
                        className='insights-drag-ghost drag-ghost cursor-grabbing'
                        style={{
                            '--ghost-top': `${dragState.mouseY - dragState.offsetY}px`,
                            '--ghost-left': `${dragState.ghostX}px`,
                            '--ghost-width': `${dragState.ghostWidth}px`,
                        } as React.CSSProperties}
                    >
                        <InsightCard
                            insight={insights[dragState.fromIndex]}
                            insightLinks={insightLinks.filter((l) => l.insightId === insights[dragState.fromIndex].id)}
                            onDragHandleMouseDown={() => { }}
                            onDragFromLink={() => { }}
                        />
                    </div>,
                    document.body,
                )}
        </CollapsibleSidePanel>
    );
}
